import { observable, computed, action, when } from 'mobx'
import { has } from 'lodash'

import BaseListItem, {
  IBaseListItemUpdateOptions,
} from 'stores/base/BaseListItem'
import ProjectStore from 'stores/project/ProjectStore'
import SessionStore from 'stores/session/SessionStore'
import ScriptStore from 'stores/script/ScriptStore'
import ModelStore from 'stores/model/ModelStore'
import ActionStore from 'stores/action/ActionStore'
import LayoutStore from 'stores/layout/LayoutStore'
import { ITabSchema } from 'stores/persistence/types'

import createError from 'utils/createError'

export interface ITabCreatePayload {
  id: string
  sessionId: string
  preview: boolean
}

export const MODEL_LIST_TAB_ID = 'model-list-tab'

class Tab extends BaseListItem {
  @observable id: ITabCreatePayload['id']
  @observable sessionId: ITabCreatePayload['sessionId']
  @observable preview: ITabCreatePayload['preview']

  private disposer: any

  constructor(tab: ITabCreatePayload) {
    super(tab)

    this.id = tab.id
    this.sessionId = tab.sessionId
    this.preview = tab.preview || false

    // Set up a one time reaction to make this tab sticky as soon as its session becomes dirty
    if (this.preview) {
      this.disposer = when(
        () => !!this.session?.isDirty,
        () => {
          this.update({ preview: false })
        }
      )
    }
  }

  @computed
  get title() {
    if (this.session?.isScript) {
      return (
        this.session.script.name ||
        (this.session.script.model && this.session.script.model.name)
      )
    }

    return '+'
  }

  @computed
  get session() {
    return SessionStore.get(this.sessionId)
  }

  @computed
  get isDirty(): boolean {
    if (!this.session) {
      return false
    }

    if (!this.session.isScript) {
      return false
    }

    const script = this.session.script!
    const actions = Object.values(ActionStore.values).filter(
      a => a.sessionId === this.sessionId
    )

    return (
      script.where.size > 0 ||
      script.fieldIds.length !== script.model.fieldIds.length ||
      script.pagination.skip !== 0 ||
      actions.length > 0
    )
  }

  @action
  save = () => {
    if (!this.session) {
      return
    }

    // Save the session
    this.session.forceSave()

    if (this.session.isScript) {
      // If this not a named script, name the script before saving
      !this.session.script.name &&
        this.session.script.update({
          name: `Copy of ${this.session.script.model.name}`,
        })
    }
  }

  @action
  discardChanges = () => {
    if (!this.session) {
      return
    }

    this.session.discardChanges()
  }

  @action
  load = ({
    modelId,
    scriptId,
    sessionId,
  }: {
    modelId?: string
    scriptId?: string
    sessionId?: string
  }) => {
    // To create a tab, we need a session
    // To create a session, we need a script
    // To create a script, we need a model
    // So the creation order goes: model > script > session (with the possibility of jumping in at any step)

    // So that's what this method allows you to do. Given at least one of `modelId`, `scriptId`, or `sessionId`, you can create a tab

    if (!sessionId && !scriptId && !modelId) {
      throw createError('Tab.load', 'Invaid params', {
        modelId,
        scriptId,
        sessionId,
      })
    }

    if (modelId) {
      const model = ModelStore.get(modelId)
      if (!model)
        throw createError('Tab.load', 'Invalid modelId', {
          modelId,
        })

      scriptId = ScriptStore.add({
        frozen: true,
        name: null,
        modelId,
        fieldIds: model.fieldIds,
      }).id
    }

    if (scriptId) {
      sessionId = SessionStore.add({
        type: 'script',
        scriptId,
        lastSavedHash: null,
      }).id
    }

    if (sessionId) {
      this.update({ sessionId })
    }

    if (ActionStore.actions.length > 0) {
      LayoutStore.updateActions({ visible: true })
    } else {
      LayoutStore.updateActions({ visible: false })
    }
  }

  @action
  update = (
    tab: Partial<ITabCreatePayload>,
    options: IBaseListItemUpdateOptions = {}
  ) => {
    has(tab, 'sessionId') && (this.sessionId = tab.sessionId!)
    has(tab, 'preview') && (this.preview = tab.preview!)

    return super.update(tab, options)
  }

  serialize = (): ITabSchema => {
    return {
      projectId: ProjectStore.activeProjectId!,

      id: this.id,
      sessionId: this.sessionId,
      preview: this.preview,
    }
  }

  @action
  clean() {
    if (this.disposer) {
      this.disposer()
    }
  }
}

export default Tab
